/**
 * RegionalRoutesModule — bridges where the inter-settlement routes cross
 * water (The Unbuilt Town wave).
 *
 * The routes themselves are terrain tint (roadW via regional_routes); this
 * module streams the BRIDGE spans: plank decks on piles with a gentle camber,
 * walkable (box colliders), built/disposed by player distance on a slow tick.
 * Deterministic per seed — every client sees the same crossings. The deck
 * clears the treaty-stone law by construction: stones sit at pair midpoints
 * on LAND (a midpoint in water would have no ground), and spans only exist
 * over water.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { bridgeSpans, type BridgeSpan } from '../lib/regional_routes.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { dressingMats } from '../lib/settlement_dressing.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { DbConnection } from '../module_bindings/index.js';

const BUILD_R = 900;
const DROP_R = 1300;
const TICK_EVERY = 120; // ~2 s at 60 fps

export class RegionalRoutesModule implements IModule {
  readonly name = 'RegionalRoutes';
  private scene!: THREE.Scene;
  private frame = 0;
  private seed = '';
  private spans: BridgeSpan[] = [];
  private built = new Map<number, { group: THREE.Group; colliderIds: string[] }>();

  constructor(private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    app.world.addSystem(makeSystem('RegionalRoutes', Phase.RenderSub, 39, () => this.tick()));
  }

  private commonsSeed(conn: DbConnection): string | null {
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private tick(): void {
    if (atlas.currentWorldId !== atlas.commonsWorldId) {
      if (this.built.size > 0) for (const id of [...this.built.keys()]) this.dispose1(id);
      return;
    }
    if (++this.frame % TICK_EVERY !== 0) return;
    const conn = atlas.conn;
    if (!conn) return;
    if (!this.seed) {
      const s = this.commonsSeed(conn);
      if (!s) return;
      this.seed = s;
      this.spans = bridgeSpans(s);
    }
    const px = atlas.localX, pz = atlas.localZ;
    this.spans.forEach((sp, i) => {
      const mx = (sp.ax + sp.bx) / 2, mz = (sp.az + sp.bz) / 2;
      const d = Math.hypot(mx - px, mz - pz) - Math.hypot(sp.bx - sp.ax, sp.bz - sp.az) / 2;
      if (d < BUILD_R && !this.built.has(i)) this.build1(i, sp);
      else if (d > DROP_R && this.built.has(i)) this.dispose1(i);
    });
  }

  private build1(i: number, sp: BridgeSpan): void {
    const M = dressingMats();
    // Deck clearance is measured from the water UNDER the span: the sea for a
    // coastal crossing, the river's surface for a river crossing (terrain wave 2026-09-08).
    const sea = sp.water ?? commonsSeaLevel(this.seed);
    const group = new THREE.Group();
    group.name = `bridge:${i}`;
    const colliderIds: string[] = [];
    const L = Math.hypot(sp.bx - sp.ax, sp.bz - sp.az);
    const yaw = Math.atan2(sp.bx - sp.ax, sp.bz - sp.az) + Math.PI / 2;
    const dx = (sp.bx - sp.ax) / L, dz = (sp.bz - sp.az) / L;
    const segL = 6;
    const n = Math.max(2, Math.ceil(L / segL));
    const deckGeo = new THREE.BoxGeometry(segL + 0.3, 0.3, 3.4);
    const deck = new THREE.InstancedMesh(deckGeo, M.wood, n);
    const pileGeo = new THREE.CylinderGeometry(0.17, 0.2, 6, 6);
    const piles = new THREE.InstancedMesh(pileGeo, M.woodDark, n * 2 + 2);
    const railGeo = new THREE.BoxGeometry(segL + 0.3, 0.08, 0.08);
    const rails = new THREE.InstancedMesh(railGeo, M.woodDark, n * 2);
    const m4 = new THREE.Matrix4();
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    let pi = 0;
    for (let k = 0; k < n; k++) {
      const t = (k + 0.5) / n;
      const x = sp.ax + (sp.bx - sp.ax) * t;
      const z = sp.az + (sp.bz - sp.az) * t;
      // Gentle camber: highest mid-span (clearance for the water surface).
      const y = sea + 1.0 + Math.sin(t * Math.PI) * Math.min(1.2, L * 0.01);
      deck.setMatrixAt(k, m4.compose(new THREE.Vector3(x, y, z), q, new THREE.Vector3(1, 1, 1)));
      const cid = `bridge:${i}:${k}`;
      this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [segL / 2 + 0.15, 0.2, 1.7] }, { x, y, z, yaw, scale: 1 });
      colliderIds.push(cid);
      for (const sgn of [-1, 1]) {
        piles.setMatrixAt(pi++, m4.compose(new THREE.Vector3(x + -dz * 1.5 * sgn, y - 3, z + dx * 1.5 * sgn), q, new THREE.Vector3(1, 1, 1)));
        rails.setMatrixAt(k * 2 + (sgn > 0 ? 1 : 0), m4.compose(new THREE.Vector3(x + -dz * 1.62 * sgn, y + 1.0, z + dx * 1.62 * sgn), q, new THREE.Vector3(1, 1, 1)));
      }
    }
    // End piles.
    piles.setMatrixAt(pi++, m4.compose(new THREE.Vector3(sp.ax, sea - 1.5, sp.az), q, new THREE.Vector3(1, 1, 1)));
    piles.setMatrixAt(pi++, m4.compose(new THREE.Vector3(sp.bx, sea - 1.5, sp.bz), q, new THREE.Vector3(1, 1, 1)));
    deck.instanceMatrix.needsUpdate = true;
    piles.instanceMatrix.needsUpdate = true;
    rails.instanceMatrix.needsUpdate = true;
    group.add(deck, piles, rails);
    for (const o of [group, deck, piles, rails]) { o.matrixAutoUpdate = false; o.updateMatrix(); } // static spans
    this.scene.add(group);
    this.built.set(i, { group, colliderIds });
  }

  private dispose1(i: number): void {
    const b = this.built.get(i);
    if (!b) return;
    for (const cid of b.colliderIds) this.physics.removeStaticCollider(cid);
    this.scene.remove(b.group);
    b.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      disposeLater(m.geometry);
      // (bug sweep) an InstancedMesh owns its instanceMatrix GPU buffer — only
      // InstancedMesh.dispose() frees it; every build/drop cycle leaked three per span.
      if ((m as THREE.InstancedMesh).isInstancedMesh) disposeLater(m as THREE.InstancedMesh);
    });
    this.built.delete(i);
  }

  dispose(): void {
    for (const id of [...this.built.keys()]) this.dispose1(id);
  }
}
